import {gql, useApolloClient, useMutation, useQuery} from '@apollo/client';
import {Box, Button, Link, Typography} from '@mui/material';
import {SnackbarKey, useSnackbar} from 'notistack';
import {format} from 'date-fns';
import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  GridCellParams,
  GridColDef,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {HistoryOutlined} from '@mui/icons-material';
import ACCOUNT_TAB from '../../../constants/AccountTab';
import Loading from '../../../components/Loading/Loading';
import {Panel} from '../../../components/Panel/Panel';
import {useAccountContext} from '../../../contexts/AccountContext';
import {ReviewContextProvider, useReviewContext} from '../../../contexts/ReviewContext';
import {useSubmitHandover} from '../../../hooks/useSubmitHandover';
import {themeColors} from '../../../theme/Theme';
import EditorGrid from '../../../components/EditorGrid/EditorGrid';
import {builderGridStyles} from '../BuilderGrid/builderStyles';
import {ShowIfDebugWrapper, useDebugContext} from '../../../contexts/DebugProvider';
import {onNewBuilderFactory} from '../ClientBuilderPage/ClientBuilder';
import {REVIEW_DETAILS_FRAGMENT} from '../../../graphql/fragments';
import {GetSppReview, GetSppReviewVariables} from '../../../schema-types/GetSppReview';
import {useEnsureSppReview} from '../../../pages/AccountInstructionsPage/AccountInstructionsPage';
import {useLocalStorage} from '../../../hooks/useLocalStorage';
import {useAccountOVBuilds} from '../hooks/useOVBuilds';
import {FeatureBoundary, hasPermission} from '../../../contexts/FeatureBoundary';
import {
  AWAITING_SUBMISSION_STATUS,
  sppBuilderColumns,
  sppDAValueGetterStatus,
} from '../BuilderGrid/sppBuilderColumns';
import {
  GlobalPermissionEnum,
  OVModelsEnum,
  OVRecommendActionEnum,
} from '../../../schema-types/globalTypes';
import HistoryDiffDialog from '../../../components/AccountReviewDialog/HistoryDiffDialog';
import {HistoryQueryType} from '../../../components/AccountReviewDialog/useHistoryDiff';
import {SPPDetailPanel} from './AccountInstructionsPage/SPPDetailPanel';
import {useAccountReviews} from '../../../hooks/useAccountReviews';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import useDjango from '../../../hooks/useDjango';
import validateUUID from '../../../utils/validateUUID';
import {
  GeneratePreSaleIllustration,
  GeneratePreSaleIllustrationVariables,
} from '../../../schema-types/GeneratePreSaleIllustration';
import {
  PreSaleIllustration,
  PreSaleIllustrationVariables,
} from '../../../schema-types/PreSaleIllustration';
import {downloadBase64File} from '../../../pages/ClientDocumentsPageV2/utilsV2';
import {ADVICE_ACCEPTED_STATUS} from '../BuilderGrid/builderColumns';

const ILLUSTRATION_POLL_INTERVAL_MS = 30000;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const illustrationFileName = (index: number, runStartedAt: Date) =>
  `pre-sale-illustration-${index + 1}-${format(runStartedAt, 'yyyyMMdd-HHmmss')}.pdf`;

export const GET_SPP_REVIEW = gql`
  query BuilderGetSppReview($accountId: UUID!) {
    sppReview(accountId: $accountId) {
      __typename
      ... on ReviewType {
        ...ReviewDetailsFragment
      }
      ... on SppReviewErrorType {
        errorMessage
      }
    }
  }
  ${REVIEW_DETAILS_FRAGMENT}
`;

export const GENERATE_PRE_SALE_ILLUSTRATIONS = gql`
  mutation GeneratePreSaleIllustration($reviewId: UUID!) {
    generatePreSaleIllustration(reviewId: $reviewId) {
      ok
      errors
    }
  }
`;

export const GET_PRE_SALE_ILLUSTRATIONS = gql`
  query PreSaleIllustration($reviewId: UUID!) {
    preSaleIllustration(reviewId: $reviewId) {
      ok
      contents
      errors
      __typename
    }
  }
`;

export const useGetSppReview = (accountId: string) => {
  const {data, loading, error} = useQuery<GetSppReview, GetSppReviewVariables>(
    GET_SPP_REVIEW,
    {
      variables: {accountId: validateUUID(accountId)!},
    }
  );
  const review = data?.sppReview?.__typename === 'ReviewType' ? data.sppReview : null;
  const errors =
    data?.sppReview?.__typename === 'SppReviewErrorType'
      ? data.sppReview.errorMessage
      : null;
  return {
    reviewId: review?.id,
    loading,
    error: errors?.join('. ') || error?.message,
    review,
  };
};

export const useLazyGetSppReview = () => {
  const {account, accountId} = useAccountContext();
  const {data, loading, error, called} = useQuery<GetSppReview, GetSppReviewVariables>(
    GET_SPP_REVIEW,
    {
      variables: {
        accountId: account?.id ? validateUUID(account.id)! : validateUUID(accountId)!,
      },
    }
  );
  const review = data?.sppReview?.__typename === 'ReviewType' ? data.sppReview : null;
  const errors =
    data?.sppReview?.__typename === 'SppReviewErrorType'
      ? data.sppReview.errorMessage
      : null;
  return {
    reviewId: review?.id,
    loading,
    error: errors?.join('. ') || error?.message,
    review,
    called,
  };
};

export const calculateAnyAwaitingSubmission = (reccs: any[]) => {
  return (
    !!reccs &&
    reccs.some(
      (x) => sppDAValueGetterStatus(undefined as never, x) === AWAITING_SUBMISSION_STATUS
    )
  );
};

export const BuilderInstructionsPage: React.FC = () => {
  const {account, accountId} = useAccountContext();
  const {data: ovBuildsData, loading: ovBuildsLoading} = useAccountOVBuilds();
  const anyAwaitingSubmission = calculateAnyAwaitingSubmission(
    ovBuildsData?.ovrecommends || []
  );
  const {reviewId, error, loading} = useEnsureSppReview(accountId!, {
    // Wait for the instructions before deciding, otherwise the empty first
    // render would let the create through before the gate can apply.
    skipCreate: ovBuildsLoading || anyAwaitingSubmission,
  });

  const [saving, setSaving] = useState(false);
  if (saving) {
    return (
      <Panel color={themeColors.lightGreen} title="Submitting instructions">
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignContent="center"
          padding={2}
        >
          <Loading />
          <Box mt={2} />
          <Typography align="center" variant="body1">
            This may take a few moments
          </Typography>
        </Box>
      </Panel>
    );
  }

  if (loading || ovBuildsLoading) {
    return <Loading entityName="instructions" />;
  }

  if (error) {
    return (
      <Panel title="Error">
        <Box>
          <Typography variant="h5">Unable to continue</Typography>
          <Typography variant="body1">Error: {error || 'No id'}</Typography>
        </Box>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </Panel>
    );
  }

  if (!reviewId) {
    return (
      <Panel title="Error">
        <Box>
          <Typography variant="h5">Create new set of instructions</Typography>
        </Box>
        <Button onClick={() => window.location.reload()}>
          Click to create set of instructions
        </Button>
      </Panel>
    );
  }

  return (
    <ReviewContextProvider forceReviewId={reviewId!}>
      <BuilderActionsPageInner saving={saving} setSaving={setSaving} />
    </ReviewContextProvider>
  );
};

export const calculateAllFinalised = (reccs: any[]) => {
  return (
    !!reccs &&
    reccs.length > 0 &&
    reccs.length === reccs.filter((x) => x.isFinalised).length
  );
};

// A Curo id alone no longer means "submitted": generating pre-sale illustrations
// also pushes every recommendation to Curo (and deliberately leaves the review
// unsubmitted), so it populates curoId too. Only `recommendationStatus` tells the
// two apart — the pre-sale run sets "Drafting advice", a real handover submit sets
// "Advice accepted" — which is the same signal the grid's status column uses.
export const calculateAllSubmitted = (reccs: any[]) => {
  return (
    !!reccs &&
    reccs.length > 0 &&
    reccs.length ===
      reccs.filter(
        (x) =>
          x.isFinalised &&
          x.recommends?.[0]?.recommendationStatus === ADVICE_ACCEPTED_STATUS
      ).length
  );
};

export const BuilderActionsPageInner: React.FC<{
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  saving: boolean;
}> = ({saving, setSaving}) => {
  const {onSPPDASubmit} = useSubmitHandover();
  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {reviewId} = useReviewContext();

  const [generatePreSaleIllustrations, {loading: generateIllustrationsLoading}] =
    useMutation<GeneratePreSaleIllustration, GeneratePreSaleIllustrationVariables>(
      GENERATE_PRE_SALE_ILLUSTRATIONS,
      {
        variables: {reviewId: validateUUID(reviewId)},
      }
    );

  const apolloClient = useApolloClient();
  const [illustrationsPending, setIllustrationsPending] = useState(false);

  const waitingSnackbarKeyRef = useRef<SnackbarKey | null>(null);
  const dismissWaitingSnackbar = () => {
    if (waitingSnackbarKeyRef.current) {
      closeSnackbar(waitingSnackbarKeyRef.current);
      waitingSnackbarKeyRef.current = null;
    }
  };

  const illustrationRunRef = useRef(0);
  const startIllustrationRun = () => {
    illustrationRunRef.current += 1;
    return illustrationRunRef.current;
  };
  const endIllustrationRun = () => {
    illustrationRunRef.current += 1;
    setIllustrationsPending(false);
    dismissWaitingSnackbar();
  };

  useEffect(
    () => () => {
      // Retire the current run so an in-flight poll cannot resume after unmount.
      illustrationRunRef.current += 1;
    },
    []
  );

  const pollForIllustrations = async (run: number, runStartedAt: Date): Promise<void> => {
    const failed = (detail: string) => {
      endIllustrationRun();
      enqueueSnackbar(`Unable to fetch pre-sale illustrations: ${detail}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    };

    let result: PreSaleIllustration['preSaleIllustration'] = null;
    try {
      const {data} = await apolloClient.query<
        PreSaleIllustration,
        PreSaleIllustrationVariables
      >({
        query: GET_PRE_SALE_ILLUSTRATIONS,
        variables: {reviewId: validateUUID(reviewId)},
        fetchPolicy: 'no-cache',
      });
      result = data?.preSaleIllustration ?? null;
    } catch (e) {
      if (illustrationRunRef.current !== run) {
        return;
      }
      failed(e instanceof Error ? e.message : 'unknown error');
      return;
    }

    if (illustrationRunRef.current !== run) {
      return;
    }

    if (!result?.ok) {
      failed(result?.errors?.join(', ') || 'unknown error');
      return;
    }

    const {contents} = result;
    if (contents.length) {
      endIllustrationRun();
      contents.forEach((contentBase64, index) => {
        downloadBase64File({
          contentBase64,
          contentType: 'application/pdf',
          fileName: illustrationFileName(index, runStartedAt),
        });
      });
      enqueueSnackbar(
        `${contents.length} pre-sale illustration${
          contents.length === 1 ? '' : 's'
        } downloaded`,
        {variant: 'success'}
      );
      return;
    }

    await sleep(ILLUSTRATION_POLL_INTERVAL_MS);
    if (illustrationRunRef.current !== run) {
      return;
    }
    await pollForIllustrations(run, runStartedAt);
  };

  const handleGenerateIllustrations = async () => {
    const failed = (detail: string) =>
      enqueueSnackbar(`Unable to generate pre-sale illustrations: ${detail}`, {
        variant: 'error',
        autoHideDuration: null,
      });

    const run = startIllustrationRun();
    const runStartedAt = new Date();
    setIllustrationsPending(false);
    dismissWaitingSnackbar();

    try {
      const res = await generatePreSaleIllustrations();
      if (illustrationRunRef.current !== run) {
        return;
      }
      const result = res.data?.generatePreSaleIllustration;
      if (!result?.ok) {
        failed(result?.errors?.join(', ') || 'unknown error');
        return;
      }
      setIllustrationsPending(true);
      waitingSnackbarKeyRef.current = enqueueSnackbar(
        'Waiting for pre-sale illustrations. This may take a few minutes and they will download automatically when ready.',
        {variant: 'info', autoHideDuration: null}
      );
    } catch (e) {
      if (illustrationRunRef.current !== run) {
        return;
      }
      failed(e instanceof Error ? e.message : 'unknown error');
      return;
    }

    await pollForIllustrations(run, runStartedAt);
  };

  const [showWarning, setShowWarning] = useState(false);
  const {data} = useAccountOVBuilds();
  const allFinalised = calculateAllFinalised(data?.ovrecommends || []);
  const allSubmitted = calculateAllSubmitted(data?.ovrecommends || []);

  const handleStartSave = () => {
    if (isSubmitting) {
      return;
    }
    setShowWarning(true);
  };
  const handleSave = () => {
    if (isSubmitting) {
      return;
    }
    setSaving(true);
    setIsSubmitting(true);

    closeSnackbar();
    // eslint-disable-next-line consistent-return
    return onSPPDASubmit({
      variables: {
        reviewId: validateUUID(reviewId)!,
      },
    })
      .then(async (res) => {
        if (res.data?.submitHandover?.ok) {
          enqueueSnackbar('Instructions submitted', {
            variant: 'success',
          });

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          setSaving(false);
          enqueueSnackbar(
            <>
              <Box>
                URL: {window.location.href}
                <Typography>Errors:</Typography>
                {res.data?.submitHandover?.errors?.map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Typography key={i}>
                    Error #{i + 1}: {x}
                  </Typography>
                ))}
                <Typography mt={3}>
                  Please{' '}
                  <Link
                    href="https://saltus.freshdesk.com/support/tickets/new"
                    target="_blank"
                    rel="noreferrer"
                    color="#fff"
                  >
                    click here
                  </Link>{' '}
                  to open a support ticket, copying and pasting the error message above.
                </Typography>
              </Box>
            </>,
            {
              variant: 'error',
              autoHideDuration: null,
            }
          );
        }
      })
      .catch(() => {
        setSaving(false);
        enqueueSnackbar('Failed to submit handover', {
          variant: 'error',
          autoHideDuration: null,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
        setShowWarning(false);
      });
  };

  return (
    <>
      <BuilderInstructionsGrid key={reviewId}>
        <ScrollableModal
          size="large"
          open={showWarning}
          onClose={() => {
            setShowWarning(false);
          }}
        >
          <Panel title="">
            <Box p={2} pt={4}>
              <Typography variant="h4">
                Please include <u>all</u> instructions before submitting.
              </Typography>
              <Box mt={4} />
              <Box display="flex">
                <Box m={1} padding={0.5}>
                  If submitting a Pension, further information will be required. Please
                  submit here
                </Box>
                <Button
                  href="https://saltuspartnersllp.freshservice.com/support/home"
                  target="_blank"
                  color="success"
                  variant="contained"
                >
                  Pension Info
                </Button>
              </Box>
              <Box mt={4} />
              <Typography variant="h6">
                If you have created a new plan, you need to include <strong>all</strong>{' '}
                associated instructions. That includes contributions, transfers and
                withdrawals.
              </Typography>
              <Box mt={4} />
              <Typography variant="h6">
                Once you submit an instruction for a new plan it needs to be set up and
                finalised by the Saltus Operations team. During this time, the new plan
                will not be available for further instructions.
              </Typography>

              <Box
                width="100%"
                mt={8}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowWarning(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSave}
                  disabled={isSubmitting || !allFinalised}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit instructions'}
                </Button>
              </Box>
            </Box>
          </Panel>
        </ScrollableModal>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleGenerateIllustrations}
            disabled={
              isSubmitting ||
              generateIllustrationsLoading ||
              illustrationsPending ||
              !allFinalised ||
              allSubmitted
            }
          >
            {generateIllustrationsLoading
              ? 'Submitting...'
              : 'Generate Pre-Sale Illustrations'}
          </Button>
          <Box ml={2} />
          {allFinalised ? null : <Typography>Drafts prevent submission</Typography>}
          <Box ml={2} />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleStartSave}
            disabled={
              isSubmitting ||
              !allFinalised ||
              generateIllustrationsLoading ||
              illustrationsPending ||
              allSubmitted
            }
          >
            {isSubmitting ? 'Submitting...' : 'Submit instructions'}
          </Button>
        </Box>
      </BuilderInstructionsGrid>
    </>
  );
};

export const BuilderInstructionsGrid: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const {djangoUser} = useDjango();
  const {accountId} = useAccountContext();
  const {debug} = useDebugContext();
  const {accountReviewsLoading} = useAccountReviews({
    variables: {
      accountId: validateUUID(accountId)!,
      idType: 'id',
      curoSync: true,
      includeProgressSync: true,
    },
  });
  const {data, error, loading} = useAccountOVBuilds();
  const navigate = useNavigate();
  const [filters, setFilters] = useLocalStorage<GridSortModel>('instructionsGrid202307', [
    {field: 'dateCreated', sort: 'desc'},
  ]);
  const onNewBuilder = onNewBuilderFactory({navigate});
  const {reviewId} = useReviewContext();
  const classesRex = builderGridStyles();
  const recommends = data?.ovrecommends || [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const columns: GridColDef[] = debug?.forms
    ? [
        {
          field: 'ID',
          valueGetter: (params, row) => {
            return row.id;
          },
        },
        ...sppBuilderColumns,
      ]
    : sppBuilderColumns;

  const handleDialogOpen = (id: string) => {
    setSelectedReviewId(id);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setSelectedReviewId('');
    setDialogOpen(false);
  };

  const builderHistoryColumn = {
    field: 'history',
    headerName: 'History',
    flex: 1,
    editable: false,
    renderCell: (params: GridCellParams) => {
      return (
        <HistoryOutlined
          style={{cursor: 'pointer'}}
          data-testid="history-review"
          onClick={() => handleDialogOpen(params.id.toString())}
        />
      );
    },
  };

  const columnsWithHistory = [
    ...columns.slice(0, -1),
    ...(hasPermission(djangoUser, [GlobalPermissionEnum.can_access_admin_features])
      ? [builderHistoryColumn]
      : []),
    ...columns.slice(-1),
  ];

  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_ehons_reqs]}
      showErrorIfDenied
    >
      <div
        style={{
          height: '100%',
          flexGrow: 1,
          position: 'relative',
        }}
      >
        <Panel color={themeColors.yellow} title="Instructions">
          <ShowIfDebugWrapper debugKey="forms">Review id: {reviewId}</ShowIfDebugWrapper>
          <Box mb={1} display="flex" justifyContent="space-between" mt={1}>
            <Box display="flex" gap={1}>
              <Button variant="contained" color="primary" onClick={onNewBuilder}>
                New instruction
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate({pathname: `../${ACCOUNT_TAB.INSTRUCTIONS}`})}
              >
                Old instructions
              </Button>
            </Box>
            {children}
          </Box>
          <Box mt={2} />
          <EditorGrid
            dataGridId="BuilderInstructionsGrid"
            sx={classesRex.dataGridRoot}
            onCellDoubleClick={(params) => {
              navigate({
                pathname: `../builder`,
                search: `ovrecommendId=${params.id}`,
              });
            }}
            columns={columnsWithHistory}
            rows={recommends}
            hideFooter
            error={error}
            sortModel={filters}
            onSortModelChange={setFilters}
            loading={loading}
            minHeight="600px"
            getDetailPanelHeight={({row}) => 'auto'}
            getDetailPanelContent={({row}: GridRowParams<OVRecommendation>) => {
              if (row.action === OVRecommendActionEnum.AD_HOC_FEE) {
                return null;
              }
              return (
                <SPPDetailPanel loading={accountReviewsLoading || loading} row={row} />
              );
            }}
            createFunc={false}
            createFuncLoading={false}
            syncFunc={() => {}}
            updateFunc={false}
          />
        </Panel>
      </div>
      <HistoryDiffDialog
        isOpen={dialogOpen}
        handleClose={handleDialogClose}
        entityId={selectedReviewId}
        entityType={OVModelsEnum.OVRECOMMEND}
        queryType={HistoryQueryType.GENERIC}
      />
    </FeatureBoundary>
  );
};
